import { useMemo, useState } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { ModelRecord } from 'spraypaint/lib-esm/model';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageLayout } from '@common/components/PageLayout';
import Table from '@common/components/table/Table';
import { ButtonPrimary } from '@common/components/buttons';
import { CheckBox } from '@common/components/inputs';
import { MainCard } from '@common/components/mainCard';
import useLegalEntities from '@common/legalEntity/useLegalEntities';
import { getActualPayslipPeriod, getMonthName } from '@common/utils/date.utils';
import { getDateFromPeriod } from '@/app/utils/dateUtils';
import EmployeePayslip from '@spraypaint/EmployeePayslip';
import PayslipReminderOrder from '@spraypaint/PayslipReminderOrder';

type Period = {
  year: number;
  month: number;
};

const employeeColumnHelper = createColumnHelper<ModelRecord<EmployeePayslip>>();
const historyColumnHelper =
  createColumnHelper<ModelRecord<PayslipReminderOrder>>();

const periodEndDate = ({ year, month }: Period): string =>
  new Date(Date.UTC(year, month, 0)).toISOString().split('T')[0];

const formatDateTime = (value: string | undefined): string =>
  value ? new Date(value).toLocaleString() : '';

const usePeriodFromSearchParams = (): Period => {
  const [searchParams] = useSearchParams();
  const defaultPeriod = getActualPayslipPeriod();
  const parsedYear = Number(searchParams.get('year'));
  const parsedMonth = Number(searchParams.get('month'));

  return {
    year:
      Number.isNaN(parsedYear) || parsedYear <= 0
        ? defaultPeriod.year
        : parsedYear,
    month:
      Number.isNaN(parsedMonth) || parsedMonth <= 0
        ? defaultPeriod.month
        : parsedMonth,
  };
};

export function PayslipAdminReminders(): JSX.Element {
  const period = usePeriodFromSearchParams();
  const queryClient = useQueryClient();
  const { legalEntities, isLoading: legalEntitiesLoading } = useLegalEntities();
  const [selectedLegalEntityIds, setSelectedLegalEntityIds] = useState<
    number[]
  >([]);

  const legalEntityNameById = useMemo(
    () =>
      legalEntities.reduce<Record<number, string>>((acc, legalEntity) => {
        acc[Number(legalEntity.id)] = legalEntity.name;
        return acc;
      }, {}),
    [legalEntities],
  );

  const periodFilter = useMemo(
    () => ({
      eq: {
        year: period.year,
        month: period.month,
      },
    }),
    [period.month, period.year],
  );

  const { data: employeesData, isLoading: employeesLoading } = useQuery(
    [
      'payslip-reminder-employees',
      period.year,
      period.month,
      selectedLegalEntityIds,
    ],
    () =>
      EmployeePayslip.where({
        period: periodFilter,
        missing: true,
        legal_entity_id: selectedLegalEntityIds,
      })
        .order({ fullName: 'asc' })
        .stats({ total: 'count' })
        .per(500)
        .all(),
    {
      enabled: selectedLegalEntityIds.length > 0,
      cacheTime: 0,
    },
  );

  const { data: historyData, isLoading: historyLoading } = useQuery(
    ['payslip-reminder-orders', period.year, period.month],
    () =>
      PayslipReminderOrder.selectExtra(['legal_entity_names', 'user_full_name'])
        .where({ payslip_period: periodFilter })
        .order({ createdAt: 'desc' })
        .per(50)
        .all(),
    { cacheTime: 0 },
  );

  const hasSelectedLegalEntities = selectedLegalEntityIds.length > 0;
  const employees = hasSelectedLegalEntities ? employeesData?.data || [] : [];
  const totalEmployees = hasSelectedLegalEntities
    ? employeesData?.meta?.stats?.total?.count || employees.length
    : 0;
  const history = historyData?.data || [];

  const lastReminderByLegalEntityId = useMemo(() => {
    const result: Record<number, ModelRecord<PayslipReminderOrder>> = {};

    history.forEach((order) => {
      order.legalEntityIds?.forEach((legalEntityId) => {
        if (!result[legalEntityId]) {
          result[legalEntityId] = order;
        }
      });
    });

    return result;
  }, [history]);

  const employeeColumns = useMemo(
    () => [
      employeeColumnHelper.accessor('fullName', { header: 'Salarié' }),
      employeeColumnHelper.accessor('legalEntityId', {
        header: 'Entité',
        cell: ({ getValue }) =>
          legalEntityNameById[Number(getValue())] || getValue(),
      }),
      employeeColumnHelper.accessor('status', { header: 'Statut' }),
    ],
    [legalEntityNameById],
  );

  const historyColumns = useMemo(
    () => [
      historyColumnHelper.accessor('createdAt', {
        header: 'Date',
        cell: ({ getValue }) => formatDateTime(getValue()),
      }),
      historyColumnHelper.accessor('payslipPeriod', {
        header: 'Période',
        cell: ({ getValue }) => {
          const payslipPeriod = getValue();
          return payslipPeriod ? getDateFromPeriod(payslipPeriod) : '';
        },
      }),
      historyColumnHelper.accessor('legalEntityNames', {
        header: 'Entités',
        cell: ({ getValue }) => getValue()?.join(', '),
      }),
      historyColumnHelper.accessor('userFullName', { header: 'Lancée par' }),
      historyColumnHelper.accessor('remindersCount', {
        header: 'Salariés relancés',
      }),
    ],
    [],
  );

  const { mutateAsync, isLoading: isCreating } = useMutation({
    mutationFn: async () => {
      const order = new PayslipReminderOrder({
        payslipPeriod: periodEndDate(period),
        legalEntityIds: selectedLegalEntityIds,
      });

      return order.saveOrThrow();
    },
    onSuccess: async () => {
      toast.success('Relance lancée');
      await queryClient.invalidateQueries(['payslip-reminder-orders']);
      await queryClient.invalidateQueries(['payslip-reminder-employees']);
    },
    onError: () => {
      toast.error('La relance n’a pas pu être lancée');
    },
  });

  const toggleLegalEntity = (legalEntityId: number, checked: boolean) => {
    setSelectedLegalEntityIds((current) =>
      checked
        ? [...current, legalEntityId]
        : current.filter((id) => id !== legalEntityId),
    );
  };

  const submitReminder = () => {
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      `Relancer ${totalEmployees} salarié(s) sur ${selectedLegalEntityIds.length} entité(s) ?`,
    );

    if (!confirmed) return;

    mutateAsync();
  };

  const title = `Relance fiches de paie - ${getMonthName(
    period.year,
    period.month,
  )} ${period.year}`;

  return (
    <PageLayout Title={title}>
      <div className="flex flex-col gap-5">
        <MainCard header="Entités à relancer">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {legalEntities.map((legalEntity) => {
              const legalEntityId = Number(legalEntity.id);
              const lastReminder = lastReminderByLegalEntityId[legalEntityId];

              return (
                <div key={legalEntity.id} className="flex flex-col gap-1">
                  <CheckBox
                    id={`payslip-reminder-legal-entity-${legalEntity.id}`}
                    checked={selectedLegalEntityIds.includes(legalEntityId)}
                    disabled={legalEntitiesLoading}
                    label={legalEntity.name}
                    onChange={(event) =>
                      toggleLegalEntity(legalEntityId, event.target.checked)
                    }
                  />
                  <span className="ml-8 text-xs text-color-medium-grey">
                    {lastReminder
                      ? `Dernière relance le ${formatDateTime(
                          lastReminder.createdAt,
                        )} par ${lastReminder.userFullName || 'inconnu'}`
                      : 'Aucune relance sur cette période'}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="flex justify-end mt-5">
            <ButtonPrimary
              onClick={submitReminder}
              disabled={selectedLegalEntityIds.length === 0}
              isLoading={isCreating}
            >
              Relancer
            </ButtonPrimary>
          </div>
        </MainCard>

        <Table
          isLoading={hasSelectedLegalEntities && employeesLoading}
          title={`Salariés à relancer (${totalEmployees})`}
          data={employees}
          columns={employeeColumns}
          disabledPagination
          emptyTableErrorMsg="Aucun salarié à relancer"
        />

        <Table
          isLoading={historyLoading}
          title="Historique des relances"
          data={history}
          columns={historyColumns}
          disabledPagination
          emptyTableErrorMsg="Aucune relance sur cette période"
        />
      </div>
    </PageLayout>
  );
}
